import { useEffect, useState } from "react";
import { backofficeApi } from "@/api";
import { FatigueStatusBadge, ScoreDisplay } from "@/components/FatigueUtils";
import { Monitor, AlertTriangle, Bell } from "lucide-react";

export default function BackOffice() {
  const [desks, setDesks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    backofficeApi.getDesks().then(r => setDesks(r.data)).finally(() => setLoading(false));
  }, []);

  const high = desks.filter(d => d.status === "high_risk" || d.status === "do_not_assign");

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="back-office-dashboard">
      <div>
        <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Module 5</div>
        <h1 className="text-2xl font-black font-heading text-foreground">Back Office Fatigue</h1>
      </div>

      {high.length > 0 && (
        <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
          <AlertTriangle size={14} className="text-red-400 shrink-0 mt-0.5" />
          <div className="text-xs text-red-300">
            <strong>{high.length} desk{high.length > 1 ? "s" : ""}</strong> at high risk. Dual-check all critical decisions. Break mandated within 30 minutes.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-3 text-center py-8 text-muted-foreground text-sm">Loading desks...</div>
        ) : desks.map(desk => (
          <div key={desk.id} className={`bg-card border rounded-sm p-4 ${desk.status === "do_not_assign" ? "border-red-500/40" : desk.status === "high_risk" ? "border-red-500/20" : "border-border"}`} data-testid={`desk-${desk.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Monitor size={14} className="text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground font-heading">{desk.name}</h3>
              </div>
              <FatigueStatusBadge status={desk.status} />
            </div>

            <div className="flex items-end gap-2 mb-4">
              <ScoreDisplay score={desk.fatigue_score} />
              <span className="text-xs text-muted-foreground mb-0.5">/ 100 cognitive load</span>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                ["Queue Size", desk.queue_size, desk.queue_size > 30 ? "text-red-400" : desk.queue_size > 15 ? "text-amber-400" : "text-foreground"],
                ["Shift Hours", `${desk.shift_hours}h`, desk.shift_hours > 9 ? "text-red-400" : desk.shift_hours > 7 ? "text-amber-400" : "text-foreground"],
                ["Interruptions", desk.interruptions, desk.interruptions > 15 ? "text-red-400" : "text-foreground"],
                ["Critical Pending", desk.critical_decisions_pending, desk.critical_decisions_pending > 2 ? "text-red-400" : "text-foreground"],
              ].map(([lbl, val, col]) => (
                <div key={lbl} className="bg-background border border-border rounded-sm p-2">
                  <div className="text-xs text-muted-foreground">{lbl}</div>
                  <div className={`text-sm font-mono font-medium mt-0.5 ${col || "text-foreground"}`}>{val}</div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Last break: {desk.last_break}</span>
              {desk.critical_decisions_pending > 0 && (
                <span className="flex items-center gap-1 text-red-400">
                  <Bell size={11} /> Dual-check required
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
